import React, { useState } from 'react';
import { ApplicationData } from '../LoanApplicationFlow';
import '../StepStyles.css';

interface Step4NSFASVerificationProps {
  data: ApplicationData;
  onUpdate: (data: Partial<ApplicationData>) => void;
  onNext: () => void;
  onBack: () => void;
}

// South African universities and colleges
const SA_INSTITUTIONS = [
  'University of Cape Town',
  'University of the Witwatersrand',
  'University of Pretoria',
  'Stellenbosch University',
  'University of KwaZulu-Natal',
  'Rhodes University',
  'University of the Free State',
  'North-West University',
  'University of the Western Cape',
  'University of Johannesburg',
  'Cape Peninsula University of Technology',
  'Durban University of Technology',
  'Tshwane University of Technology',
  'Vaal University of Technology',
  'Central University of Technology',
  'Mangosuthu University of Technology',
  'Walter Sisulu University',
  'University of Fort Hare',
  'University of Limpopo',
  'University of Zululand',
  'University of Venda',
  'Sol Plaatje University',
  'University of Mpumalanga',
  'Sefako Makgatho Health Sciences University',
  'Other'
];

interface FileUpload {
  file: File | null;
  preview: string | null;
  error: string | null;
}

const Step4NSFASVerification: React.FC<Step4NSFASVerificationProps> = ({
  data,
  onUpdate,
  onNext,
  onBack
}) => {
  const [formData, setFormData] = useState<{
    institutionName: string;
    studentNumber: string;
    studyYear: string;
    nsfasMonthlyAllowance: string;
    hasOtherCredit: '' | 'yes' | 'no';
    otherCreditMonthly: string;
  }>({
    institutionName: data.institutionName,
    studentNumber: data.studentNumber,
    studyYear: data.studyYear || '',
    nsfasMonthlyAllowance: data.nsfasMonthlyAllowance || '',
    hasOtherCredit: data.hasOtherCredit || '',
    otherCreditMonthly: data.otherCreditMonthly || '',
  });
  
  const [fileUploads, setFileUploads] = useState<{
    nsfasStatement: FileUpload;
    bankStatement: FileUpload;
    studentCard: FileUpload;
  }>({
    nsfasStatement: { file: null, preview: null, error: null },
    bankStatement: { file: null, preview: null, error: null },
    studentCard: { file: null, preview: null, error: null }
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValidating, setIsValidating] = useState(false);

  // File validation
  const validateFile = (file: File): { isValid: boolean; error: string | null } => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    
    if (file.size > maxSize) {
      return { isValid: false, error: 'File size must be less than 5MB' };
    }
    
    if (!allowedTypes.includes(file.type)) {
      return { isValid: false, error: 'Only JPG, PNG, and PDF files are allowed' };
    }
    
    return { isValid: true, error: null };
  };

  // Handle file upload
  const handleFileUpload = (fileType: keyof typeof fileUploads, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    if (!file) return;
    
    const validation = validateFile(file);
    
    if (!validation.isValid) {
      setFileUploads(prev => ({
        ...prev,
        [fileType]: { file: null, preview: null, error: validation.error }
      }));
      return;
    }
    
    // Create preview for images
    let preview: string | null = null;
    if (file.type.startsWith('image/')) {
      preview = URL.createObjectURL(file);
    }
    
    setFileUploads(prev => ({
      ...prev,
      [fileType]: { file, preview, error: null }
    }));
    
    // Update parent data
    onUpdate({ [fileType]: file });
  };

  // Remove file
  const removeFile = (fileType: keyof typeof fileUploads) => {
    const currentFile = fileUploads[fileType];
    if (currentFile.preview) {
      URL.revokeObjectURL(currentFile.preview);
    }
    
    setFileUploads(prev => ({
      ...prev,
      [fileType]: { file: null, preview: null, error: null }
    }));
    
    // Update parent data
    onUpdate({ [fileType]: undefined });
  };

  // Handle input changes
  const handleInputChange = (
    field: 'institutionName' | 'studentNumber' | 'studyYear' | 'nsfasMonthlyAllowance' | 'hasOtherCredit' | 'otherCreditMonthly',
    value: string
  ) => {
    if (field === 'hasOtherCredit') {
      const credit: '' | 'yes' | 'no' = value === 'yes' || value === 'no' ? value : '';
      setFormData(prev => ({ ...prev, hasOtherCredit: credit }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Update parent data
    onUpdate({ [field]: value });
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Institution validation
    if (!formData.institutionName.trim()) {
      newErrors.institutionName = 'Please select your institution';
    }
    
    // Student number validation
    if (!formData.studentNumber.trim()) {
      newErrors.studentNumber = 'Student number is required';
    } else if (formData.studentNumber.trim().length < 6) {
      newErrors.studentNumber = 'Student number must be at least 6 characters';
    }

    if (!formData.studyYear) {
      newErrors.studyYear = 'Please select your year of study';
    }

    const allowance = parseFloat(formData.nsfasMonthlyAllowance);
    if (!formData.nsfasMonthlyAllowance.trim() || !Number.isFinite(allowance) || allowance < 0) {
      newErrors.nsfasMonthlyAllowance = 'Enter your monthly NSFAS allowance';
    }

    if (formData.hasOtherCredit !== 'yes' && formData.hasOtherCredit !== 'no') {
      newErrors.hasOtherCredit = 'Please answer this question';
    }

    if (formData.hasOtherCredit === 'yes') {
      const other = parseFloat(formData.otherCreditMonthly);
      if (!formData.otherCreditMonthly.trim() || !Number.isFinite(other) || other <= 0) {
        newErrors.otherCreditMonthly = 'Enter your monthly credit repayments';
      }
    }
    
    // File upload validation
    if (!fileUploads.nsfasStatement.file) {
      newErrors.nsfasStatement = 'NSFAS statement is required';
    }
    
    if (!fileUploads.bankStatement.file) {
      newErrors.bankStatement = 'Bank statement is required';
    }
    
    if (!fileUploads.studentCard.file) {
      newErrors.studentCard = 'Student card is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle continue
  const handleContinue = async () => {
    setIsValidating(true);
    
    if (validateForm()) {
      // Update parent with current data
      onUpdate({
        ...formData,
        nsfasStatement: fileUploads.nsfasStatement.file || undefined,
        bankStatement: fileUploads.bankStatement.file || undefined,
        studentCard: fileUploads.studentCard.file || undefined
      });
      
      // Simulate file upload
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      onNext();
    }
    
    setIsValidating(false);
  };

  // File upload component
  const FileUploadComponent: React.FC<{
    title: string;
    description: string;
    fileType: keyof typeof fileUploads;
    required?: boolean;
  }> = ({ title, description, fileType, required = true }) => {
    const upload = fileUploads[fileType];
    
    return (
      <div className="file-upload-section">
        <div className="file-upload-header">
          <h4 className="file-upload-title">
            {title} {required && <span className="required">*</span>}
          </h4>
          <p className="file-upload-description">{description}</p>
        </div>
        
        {!upload.file ? (
          <div className="file-upload-area">
            <input
              type="file"
              id={`file-${fileType}`}
              className="file-input"
              accept="image/*,.pdf"
              onChange={(e) => handleFileUpload(fileType, e)}
            />
            <label htmlFor={`file-${fileType}`} className="file-upload-label">
              <div className="upload-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14,2 14,8 20,8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10,9 9,9 8,9"/>
                </svg>
              </div>
              <div className="upload-text">
                <span className="upload-primary">Click to upload</span>
                <span className="upload-secondary">or drag and drop</span>
              </div>
              <div className="upload-formats">JPG, PNG, PDF (max 5MB)</div>
            </label>
          </div>
        ) : (
          <div className="file-uploaded">
            <div className="file-success-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <polyline points="9,12 11,14 16,9"/>
              </svg>
            </div>
            <div className="file-info">
              <div className="file-name">{upload.file.name}</div>
              <div className="file-size">
                {(upload.file.size / 1024 / 1024).toFixed(2)} MB
              </div>
            </div>
            <div className="file-actions">
              {upload.preview && (
                <button
                  type="button"
                  className="file-action-btn view"
                  onClick={() => upload.preview && window.open(upload.preview, '_blank')}
                >
                  👁 View
                </button>
              )}
              <button
                type="button"
                className="file-action-btn remove"
                onClick={() => removeFile(fileType)}
              >
                🗑 Remove
              </button>
            </div>
          </div>
        )}
        
        {upload.error && (
          <div className="file-error">{upload.error}</div>
        )}
        
        {errors[fileType] && (
          <div className="file-error">{errors[fileType]}</div>
        )}
      </div>
    );
  };

  return (
    <div className="step-container">
      <div className="step-header">
        <h2 className="step-title">NSFAS Verification</h2>
        <p className="step-description">
          Upload your NSFAS and student documents to verify your eligibility. 
          All documents will be reviewed within 24 hours.
        </p>
      </div>

      <div className="step-content">
        {/* Institution Details */}
        <div className="institution-section">
          <h3 className="section-title">Institution Details</h3>
          
          <div className="detail-grid">
            <div className="form-group">
              <label htmlFor="institutionName" className="form-label">
                Institution Name *
              </label>
              <select
                id="institutionName"
                className={`form-select ${errors.institutionName ? 'error' : ''}`}
                value={formData.institutionName}
                onChange={(e) => handleInputChange('institutionName', e.target.value)}
              >
                <option value="">Select your institution</option>
                {SA_INSTITUTIONS.map((institution) => (
                  <option key={institution} value={institution}>
                    {institution}
                  </option>
                ))}
              </select>
              {errors.institutionName && (
                <span className="error-message">{errors.institutionName}</span>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="studentNumber" className="form-label">
                Student Number *
              </label>
              <input
                type="text"
                id="studentNumber"
                className={`form-input ${errors.studentNumber ? 'error' : ''}`}
                value={formData.studentNumber}
                onChange={(e) => handleInputChange('studentNumber', e.target.value)}
                placeholder="e.g., 123456789"
              />
              {errors.studentNumber && (
                <span className="error-message">{errors.studentNumber}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="studyYear" className="form-label">
                Year of study *
              </label>
              <select
                id="studyYear"
                className={`form-select ${errors.studyYear ? 'error' : ''}`}
                value={formData.studyYear}
                onChange={(e) => handleInputChange('studyYear', e.target.value)}
              >
                <option value="">Select year</option>
                <option value="1st year">1st year</option>
                <option value="2nd year">2nd year</option>
                <option value="3rd year">3rd year</option>
                <option value="4th year">4th year</option>
                <option value="Honours">Honours</option>
                <option value="Postgraduate">Postgraduate</option>
              </select>
              {errors.studyYear && (
                <span className="error-message">{errors.studyYear}</span>
              )}
            </div>
          </div>
        </div>

        <div className="affordability-section">
          <h3 className="section-title">Allowance and credit</h3>
          <p className="affordability-note">
            These answers help us check the loan is affordable. They do not change the amount you can apply for.
          </p>

          <div className="form-group">
            <label htmlFor="nsfasMonthlyAllowance" className="form-label">
              Monthly NSFAS allowance (living, transport and personal care) *
            </label>
            <div className="money-input">
              <span className="money-prefix">R</span>
              <input
                type="text"
                inputMode="decimal"
                id="nsfasMonthlyAllowance"
                className={`form-input ${errors.nsfasMonthlyAllowance ? 'error' : ''}`}
                value={formData.nsfasMonthlyAllowance}
                onChange={(e) => handleInputChange('nsfasMonthlyAllowance', e.target.value.replace(/[^\d.]/g, ''))}
                placeholder="1500"
              />
            </div>
            {errors.nsfasMonthlyAllowance && (
              <span className="error-message">{errors.nsfasMonthlyAllowance}</span>
            )}
          </div>

          <div className="form-group">
            <span className="form-label">Do you have other credit repayments? *</span>
            <div className="choice-row">
              <label className={`choice-pill ${formData.hasOtherCredit === 'yes' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="hasOtherCredit"
                  checked={formData.hasOtherCredit === 'yes'}
                  onChange={() => handleInputChange('hasOtherCredit', 'yes')}
                />
                <span>Yes</span>
              </label>
              <label className={`choice-pill ${formData.hasOtherCredit === 'no' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="hasOtherCredit"
                  checked={formData.hasOtherCredit === 'no'}
                  onChange={() => {
                    handleInputChange('hasOtherCredit', 'no');
                    handleInputChange('otherCreditMonthly', '');
                  }}
                />
                <span>No</span>
              </label>
            </div>
            {errors.hasOtherCredit && (
              <span className="error-message">{errors.hasOtherCredit}</span>
            )}
          </div>

          {formData.hasOtherCredit === 'yes' && (
            <div className="form-group">
              <label htmlFor="otherCreditMonthly" className="form-label">
                Monthly repayments on that credit *
              </label>
              <div className="money-input">
                <span className="money-prefix">R</span>
                <input
                  type="text"
                  inputMode="decimal"
                  id="otherCreditMonthly"
                  className={`form-input ${errors.otherCreditMonthly ? 'error' : ''}`}
                  value={formData.otherCreditMonthly}
                  onChange={(e) => handleInputChange('otherCreditMonthly', e.target.value.replace(/[^\d.]/g, ''))}
                  placeholder="500"
                />
              </div>
              {errors.otherCreditMonthly && (
                <span className="error-message">{errors.otherCreditMonthly}</span>
              )}
            </div>
          )}
        </div>

        {/* Document Uploads */}
        <div className="documents-section">
          <h3 className="section-title">Required Documents</h3>
          
          <FileUploadComponent
            title="NSFAS Statement"
            description="Recent NSFAS statement showing your allowance details and payment schedule"
            fileType="nsfasStatement"
          />
          
          <FileUploadComponent
            title="Bank Statement"
            description="Recent bank statement (last 3 months) showing NSFAS payments received"
            fileType="bankStatement"
          />
          
          <FileUploadComponent
            title="Student Card"
            description="Current student card or proof of enrollment from your institution"
            fileType="studentCard"
          />
        </div>

        {/* Verification Process Info */}
        <div className="verification-process">
          <div className="process-header">
            <div className="process-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 11l3 3l8-8"/>
                <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9s4.03-9 9-9c1.51 0 2.93.37 4.18 1.03"/>
              </svg>
            </div>
            <div className="process-title">
              <h4>Verification Process</h4>
              <p>Your documents will be processed securely and efficiently</p>
            </div>
          </div>
          
          <div className="process-steps">
            <div className="process-step">
              <div className="step-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14,2 14,8 20,8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
              </div>
              <div className="step-content">
                <h5>Document Review</h5>
                <p>Our verification team reviews your documents within <strong>24 hours</strong></p>
              </div>
            </div>
            
            <div className="process-step">
              <div className="step-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="8.5" cy="7" r="4"/>
                  <polyline points="17,11 19,13 23,9"/>
                </svg>
              </div>
              <div className="step-content">
                <h5>NSFAS Verification</h5>
                <p>We verify your beneficiary status and payment schedule</p>
              </div>
            </div>
            
            <div className="process-step">
              <div className="step-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                  <path d="M6 12v5c3 3 9 3 12 0v-5"/>
                </svg>
              </div>
              <div className="step-content">
                <h5>Institution Confirmation</h5>
                <p>Your student registration status is confirmed with your institution</p>
              </div>
            </div>
            
            <div className="process-step">
              <div className="step-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
                </svg>
              </div>
              <div className="step-content">
                <h5>Real-time Updates</h5>
                <p>You'll receive SMS and email updates on your verification status</p>
              </div>
            </div>
            
            <div className="process-step final">
              <div className="step-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="16,12 12,8 8,12"/>
                  <line x1="12" y1="16" x2="12" y2="8"/>
                </svg>
              </div>
              <div className="step-content">
                <h5>Instant Processing</h5>
                <p>Once verified, your loan will be <strong>processed immediately</strong></p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step Actions */}
      <div className="step-actions">
        <button
          className="back-btn secondary"
          onClick={onBack}
          type="button"
        >
          Back
        </button>
        
        <button
          className="continue-btn primary"
          onClick={handleContinue}
          disabled={isValidating}
          type="button"
        >
          {isValidating ? (
            <>
              <span className="btn-spinner"></span>
              Uploading Documents...
            </>
          ) : (
            'Continue'
          )}
        </button>
      </div>
    </div>
  );
};

export default Step4NSFASVerification;

